import axios, { isAxiosError, type AxiosError } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

// VITE_API_URL is the API origin (e.g. https://api.example.com); the fallback is the local
// dev API. A trailing slash or a "/api/v1" suffix is stripped so both conventions work.
const RAW_API_URL = import.meta.env['VITE_API_URL'] as string | undefined;
export const BASE_URL: string = (RAW_API_URL ?? 'http://localhost:3001')
  .trim()
  .replace(/\/+$/, '')
  .replace(/\/api\/v1$/i, '');

export const apiClient = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

interface RefreshWaiter {
  resolve: (token: string) => void;
  reject: () => void;
}

let isRefreshing = false;
let refreshQueue: RefreshWaiter[] = [];

// Settles every request that was waiting on the refresh: retried with the new token, or
// failed with its own 401 when there is none. Nothing is ever left pending or replayed later.
function settleQueue(token: string | null) {
  const waiters = refreshQueue;
  refreshQueue = [];
  for (const w of waiters) {
    if (token) w.resolve(token);
    else w.reject();
  }
}

// Signing out while a refresh is in flight: fail the waiters now, so they are not replayed
// under whoever signs in next.
useAuthStore.subscribe((state, prev) => {
  if (prev.isAuthenticated && !state.isAuthenticated) settleQueue(null);
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && originalRequest && !('_retry' in originalRequest)) {
      // One retry per request, whichever path it takes — a second 401 is final
      Object.assign(originalRequest, { _retry: true });

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: () => {
              reject(error);
            },
          });
        });
      }

      isRefreshing = true;
      const { refreshToken, setTokens, logout } = useAuthStore.getState();
      if (!refreshToken) {
        isRefreshing = false;
        logout();
        throw error;
      }

      let accessToken: string;
      try {
        // Plain axios, not apiClient: a 401 here must not re-enter this interceptor
        const res = await axios.post<{
          data: { accessToken: string; refreshToken: string };
        }>(`${BASE_URL}/api/v1/auth/refresh`, { refreshToken }, { timeout: 15_000 });
        if (useAuthStore.getState().refreshToken !== refreshToken) {
          // Signed out (or in as someone else) meanwhile — this result belongs to nobody
          settleQueue(null);
          throw error;
        }
        accessToken = res.data.data.accessToken;
        setTokens(accessToken, res.data.data.refreshToken);
      } catch (refreshErr) {
        settleQueue(null);
        // Only a rejected session ends the login. A timeout, 429 or 5xx just fails this
        // request; the next 401 tries the refresh again.
        const status = isAxiosError(refreshErr) ? refreshErr.response?.status : undefined;
        const sessionRejected = status === 401 || status === 403;
        if (sessionRejected && useAuthStore.getState().refreshToken === refreshToken) logout();
        throw error;
      } finally {
        isRefreshing = false;
      }

      settleQueue(accessToken);
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return apiClient(originalRequest);
    }
    throw error;
  },
);
